import { useAuth0 } from "@auth0/auth0-react";
import React from "react";
import { useBranding } from "src/components/Providers/BrandingProvider";
import * as OrgConfig from "src/generated/hooks/org-config/requests";
import { useAuth0ClientId } from "src/hooks/useAuth0ClientId";
import { useOrganizationPath } from "src/hooks/useOrganizationPath";
import { useEnv } from "src/services/env";
import { isNonEmptyString, isNullOrUndefined } from "src/services/predicates";
import * as RD from "src/types/remoteData";
import { z } from "zod";
import { useIsSsoOrg } from "./useIsSsoOrg";
import { useOrgConfig } from "./useOrgConfig";

export class NeedReauthenticateError extends Error {}

export const MFA_SCOPES = "enroll read:authenticators remove:authenticators";

function toMfaApiError(status: number, body: unknown): Error {
  const parsed = Auth0ErrorSchema.safeParse(body);
  const message = parsed.success
    ? parsed.data.error_description ??
      parsed.data.message ??
      parsed.data.error ??
      "Auth0 MFA request failed"
    : "Auth0 MFA request failed";

  return new Error(`${message} (${status})`);
}
export const FactorSchema = z.object({
  active: z.boolean(),
  id: z.string(),
  authenticator_type: z.string(),
  oob_channel: z.string().optional(),
  name: z.string().optional(),
});
export type Factor = z.infer<typeof FactorSchema>;

export const OtpEnrollmentSchema = z.object({
  authenticator_type: z.literal("otp"),
  secret: z.string().min(1),
  barcode_uri: z.string().min(1),
});
export type OtpEnrollment = z.infer<typeof OtpEnrollmentSchema>;

export const PhoneEnrollmentSchema = z.object({
  authenticator_type: z.literal("oob"),
  oob_channel: z.literal("sms"),
  oob_code: z.string(),
});
export type PhoneEnrollment = z.infer<typeof PhoneEnrollmentSchema>;

const Auth0ErrorSchema = z.object({
  error: z.string().optional(),
  error_description: z.string().optional(),
  message: z.string().optional(),
});

export function getMfaAudience(domain: string): string {
  return `https://${domain}/mfa/`;
}

export async function fetchMfaFactors(
  domain: string,
  token: string
): Promise<Factor[]> {
  const response = await fetch(`https://${domain}/mfa/authenticators`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const body: unknown = await response.json();
  if (!response.ok) {
    throw toMfaApiError(response.status, body);
  }

  return z.array(FactorSchema).parse(body);
}

export async function enrollOtp(
  domain: string,
  token: string
): Promise<OtpEnrollment> {
  const response = await fetch(`https://${domain}/mfa/associate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ authenticator_types: ["otp"] }),
  });

  const body: unknown = await response.json();
  if (!response.ok) {
    throw toMfaApiError(response.status, body);
  }

  return OtpEnrollmentSchema.parse(body);
}

export async function confirmOtp(
  domain: string,
  clientId: string,
  token: string,
  otp: string
): Promise<string> {
  const response = await fetch(`https://${domain}/oauth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "http://auth0.com/oauth/grant-type/mfa-otp",
      client_id: clientId,
      mfa_token: token,
      otp,
    }),
  });

  const body: unknown = await response.json();
  if (!response.ok) {
    throw toMfaApiError(response.status, body);
  }

  return z.object({ access_token: z.string().min(1) }).parse(body).access_token;
}

export async function deleteFactor(
  domain: string,
  id: string,
  token: string
): Promise<void> {
  const response = await fetch(`https://${domain}/mfa/authenticators/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw toMfaApiError(response.status, undefined);
  }
}

export async function enrollPhone(
  domain: string,
  token: string,
  phoneNumber: string
): Promise<PhoneEnrollment> {
  const response = await fetch(`https://${domain}/mfa/associate`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      authenticator_types: ["oob"],
      oob_channels: ["sms"],
      phone_number: phoneNumber,
    }),
  });

  const body: unknown = await response.json();
  if (!response.ok) {
    throw toMfaApiError(response.status, body);
  }

  return PhoneEnrollmentSchema.parse(body);
}

export async function confirmPhone(args: {
  domain: string;
  clientId: string;
  token: string;
  verificationCode: string;
  oobCode: string;
}): Promise<string> {
  const { domain, clientId, token, verificationCode, oobCode } = args;
  const response = await fetch(`https://${domain}/oauth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "http://auth0.com/oauth/grant-type/mfa-oob",
      client_id: clientId,
      mfa_token: token,
      oob_code: oobCode,
      binding_code: verificationCode,
    }),
  });

  const body: unknown = await response.json();
  if (!response.ok) {
    throw toMfaApiError(response.status, body);
  }

  return z.object({ access_token: z.string().min(1) }).parse(body).access_token;
}

export const useMfaApi = () => {
  const env = useEnv();
  const auth0 = useAuth0();
  const clientId = useAuth0ClientId();
  const avelaOrganization = useOrganizationPath();
  const branding = useBranding();
  const reauthenticate = React.useCallback(
    async (returnTo?: string) => {
      if (!isNonEmptyString(env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN)) {
        throw new Error("Missing VITE_AUTH0_DEFAULT_TENANT_DOMAIN");
      }

      if (!isNonEmptyString(avelaOrganization)) {
        throw new Error("Missing organization");
      }

      await auth0.loginWithRedirect({
        isMfaStepUp: true,
        audience: getMfaAudience(env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN),
        scope: MFA_SCOPES,
        isParent: false,
        avelaOrganization: avelaOrganization,
        branding: JSON.stringify(branding),
        appState: {
          returnTo: returnTo ?? window.location.href,
        },
      });
    },
    [auth0, branding, env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN, avelaOrganization]
  );

  const _getIdToken = React.useCallback(async () => {
    if (!isNonEmptyString(env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN)) {
      throw new Error("Missing VITE_AUTH0_DEFAULT_TENANT_DOMAIN");
    }

    const idToken = await auth0.getIdTokenClaims({
      audience: getMfaAudience(env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN),
      scope: MFA_SCOPES,
    });
    return idToken;
  }, [auth0, env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN]);

  const hasMfaToken = React.useCallback(async () => {
    const idToken = await _getIdToken();
    return !isNullOrUndefined(idToken);
  }, [_getIdToken]);

  const hasMfaIdToken = React.useCallback(async () => {
    const idToken = await _getIdToken();
    return Array.isArray(idToken?.amr) && idToken.amr.includes("mfa");
  }, [_getIdToken]);

  const getMfaIdToken = React.useCallback(async () => {
    const idToken = await _getIdToken();
    if (
      isNullOrUndefined(idToken) ||
      !Array.isArray(idToken.amr) ||
      !idToken.amr.includes("mfa")
    ) {
      throw new NeedReauthenticateError();
    }
    return idToken.__raw;
  }, [_getIdToken]);

  const getMfaAccessToken = React.useCallback(async () => {
    if (!(await hasMfaToken())) {
      throw new NeedReauthenticateError();
    }

    if (!isNonEmptyString(env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN)) {
      throw new Error("Missing VITE_AUTH0_DEFAULT_TENANT_DOMAIN");
    }

    try {
      return await auth0.getAccessTokenSilently({
        isMfaStepUp: true,
        audience: getMfaAudience(env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN),
        scope: MFA_SCOPES,
        isParent: false,
        avelaOrganization: avelaOrganization,
      });
    } catch (error) {
      console.error(error);
      throw new NeedReauthenticateError();
    }
  }, [
    auth0,
    env.VITE_AUTH0_DEFAULT_TENANT_DOMAIN,
    hasMfaToken,
    avelaOrganization,
  ]);

  const orgConfigRD = useOrgConfig<OrgConfig.MfaConfig>(
    "Mfa",
    OrgConfig.MfaConfigSchema.default
  );
  const isSsoOrgRD = useIsSsoOrg();
  const isMfaEnabledRemoteData = React.useMemo(() => {
    return RD.map2(
      orgConfigRD,
      isSsoOrgRD,
      (orgConfig, isSsoOrg) => orgConfig.disabled !== true && !isSsoOrg
    );
  }, [orgConfigRD, isSsoOrgRD]);

  const api = React.useMemo(
    () => ({
      isMfaEnabledRemoteData,
      hasMfaToken,
      hasMfaIdToken,
      reauthenticate,
      getMfaAccessToken,
      getMfaIdToken,
      fetchMfaFactors: async () => {
        const token = await getMfaAccessToken();
        return fetchMfaFactors(env.VITE_AUTH0_DOMAIN, token);
      },
      enrollOtp: async () => {
        const token = await getMfaAccessToken();
        return enrollOtp(env.VITE_AUTH0_DOMAIN, token);
      },
      confirmOtp: async (otp: string) => {
        if (!clientId.hasData()) {
          throw new Error("Missing Auth0 clientId");
        }
        const token = await getMfaAccessToken();
        return confirmOtp(
          env.VITE_AUTH0_DOMAIN,
          clientId.data.clientId,
          token,
          otp
        );
      },
      deleteFactor: async (id: string) => {
        const token = await getMfaAccessToken();
        await deleteFactor(env.VITE_AUTH0_DOMAIN, id, token);
      },
      enrollPhone: async (phoneNumber: string) => {
        const token = await getMfaAccessToken();
        return enrollPhone(env.VITE_AUTH0_DOMAIN, token, phoneNumber);
      },
      confirmPhone: async (args: {
        verificationCode: string;
        oobCode: string;
      }) => {
        if (!clientId.hasData()) {
          throw new Error("Missing Auth0 clientId");
        }
        const token = await getMfaAccessToken();
        return confirmPhone({
          domain: env.VITE_AUTH0_DOMAIN,
          clientId: clientId.data.clientId,
          token,
          ...args,
        });
      },
    }),
    [
      clientId,
      env.VITE_AUTH0_DOMAIN,
      getMfaAccessToken,
      getMfaIdToken,
      hasMfaToken,
      hasMfaIdToken,
      reauthenticate,
      isMfaEnabledRemoteData,
    ]
  );

  return api;
};
